import { canonicalJson, generateMacroPackageMessage, MacroPackageMessageType, macroPackageVerificationRequestSchema,
  macroPackageLockedValidationCommandSchema, type ConnectedHost, type MacroPackageLockedValidationCommand,
  type MacroPackageRunReply, type WebsocketMessage } from '@playcastdotio/Messaging';
import type { NativeMacroPackageBridge } from './NativeMacroPackageBridge';
import type { IMessageRouter } from '../MessageRouter';
import type { IRealtimeService } from '../realtime/IRealtimeService';

interface OwnedValidation { request: MacroPackageLockedValidationCommand; startRequestId: string; draining: boolean; started: boolean }
export interface MacroPackageVerificationRelayDependencies {
  native: Pick<NativeMacroPackageBridge, 'requestOwned' | 'onReply' | 'onDisconnected' | 'capabilities' | 'cancel'>;
  realtime: Pick<IRealtimeService, 'trySendMessage' | 'onDisconnected' | 'isConnected'>;
  /** Reads the actual active control owner; packet-selectedHost alone is never authority. */
  owns(host: ConnectedHost): boolean;
}

/** Only authenticated realtime commands reach native; replies cannot be queued into a later connection. */
export class MacroPackageVerificationRelay {
  private readonly owned = new Map<string, OwnedValidation>();
  private generation = 0;
  constructor(private readonly deps: MacroPackageVerificationRelayDependencies, router: IMessageRouter) {
    router.register(MacroPackageMessageType.VERIFY_REQUEST, input => { void this.verify(input); });
    router.register(MacroPackageMessageType.LOCKED_VALIDATION_COMMAND, input => { void this.validate(input); });
    deps.native.onReply(reply => this.nativeReply(reply));
    deps.native.onDisconnected(() => { this.generation++; for (const value of this.owned.values()) value.draining = true; });
    deps.realtime.onDisconnected(() => { void this.release().catch(() => undefined); });
  }

  /** Called by actual local/admin owner lifecycle before it can be replaced. */
  async release(host?: ConnectedHost): Promise<void> {
    this.generation++;
    const draining = [...this.owned.values()].filter(value => !host || canonicalJson(host) === canonicalJson(value.request.selectedHost));
    await Promise.all(draining.map(async value => {
      value.draining = true;
      if (!value.started) { this.owned.delete(value.request.validationId); return; }
      const reply = await this.deps.native.cancel(value.request.validationId);
      if (reply.outcome !== 'cancelled' || reply.errorCode) throw new Error('input_release_unconfirmed');
      this.owned.delete(value.request.validationId);
    }));
  }

  private async verify(input: WebsocketMessage): Promise<void> {
    const parsed = macroPackageVerificationRequestSchema.safeParse(input);
    if (!parsed.success || !this.authorized(parsed.data.selectedHost)) return;
    const request = parsed.data, generation = this.generation;
    try {
      const reply = await this.deps.native.requestOwned(request.command);
      if (generation === this.generation && this.authorized(request.selectedHost)) this.deps.realtime.trySendMessage(generateMacroPackageMessage({
        type: MacroPackageMessageType.VERIFY_RESPONSE, selectedHost: request.selectedHost, reply }));
    } catch { /* The server's bounded timeout reports an unconfirmed native preflight. */ }
  }

  private async validate(input: WebsocketMessage): Promise<void> {
    const parsed = macroPackageLockedValidationCommandSchema.safeParse(input);
    if (!parsed.success || !this.authorized(parsed.data.selectedHost)) return;
    const request = parsed.data, generation = this.generation, command = request.command;
    let owned = this.owned.get(request.validationId);
    if (command.command === 'start') {
      if (owned || [...this.owned.values()].some(value => value.draining)) return;
      owned = { request, startRequestId: command.requestId, draining: false, started: false };
      this.owned.set(request.validationId, owned);
      try {
        const capabilities = await this.deps.native.capabilities();
        if (capabilities.outcome === 'failed' && capabilities.errorCode) {
          this.forward(request, this.failed(request, capabilities.errorCode));
          this.owned.delete(request.validationId); return;
        }
        if (generation !== this.generation || !this.authorized(request.selectedHost)
          || !capabilities.host || canonicalJson(capabilities.host) !== canonicalJson(request.validation.selectedHost)) {
          this.forward(request, this.failed(request, 'host_validation_target_changed'));
          this.owned.delete(request.validationId); return;
        }
      } catch { this.owned.delete(request.validationId); return; }
    } else if (!owned || canonicalJson(owned.request.validation) !== canonicalJson(request.validation)
      || canonicalJson(owned.request.selectedHost) !== canonicalJson(request.selectedHost)
      || (owned.draining && command.command !== 'cancel')) return;
    try {
      if (command.command === 'start') owned.started = true;
      const reply = await this.deps.native.requestOwned(command);
      if (generation !== this.generation || !this.authorized(request.selectedHost)) {
        if (command.command === 'start') await this.deps.native.cancel(request.validationId);
        return;
      }
      this.forward(request, reply);
      this.settle(owned, reply, command.command === 'cancel');
    } catch {
      if (owned.started && (command.command === 'start' || command.command === 'cancel')) owned.draining = true;
      // No admission or successful release is fabricated after transport uncertainty.
    }
  }

  private nativeReply(reply: MacroPackageRunReply): void {
    const owned = this.owned.get(reply.runId);
    if (!owned || reply.requestId !== owned.startRequestId || owned.draining) return;
    if (!this.authorized(owned.request.selectedHost)) { void this.release(owned.request.selectedHost).catch(() => undefined); return; }
    this.forward(owned.request, reply);
    this.settle(owned, reply);
  }
  private forward(request: MacroPackageLockedValidationCommand, reply: MacroPackageRunReply): void {
    this.deps.realtime.trySendMessage(generateMacroPackageMessage({ type: MacroPackageMessageType.LOCKED_VALIDATION_REPLY,
      selectedHost: request.selectedHost, validationId: request.validationId, validation: request.validation, reply }));
  }
  private authorized(host: ConnectedHost): boolean { return this.deps.realtime.isConnected() && this.deps.owns(host); }
  private settle(owned: OwnedValidation, reply: MacroPackageRunReply, cancellation = false): void {
    if (this.owned.get(owned.request.validationId) !== owned) return;
    if (cancellation ? reply.outcome !== 'cancelled' || !!reply.errorCode
      : ['input_release_unconfirmed', 'native_terminal_unconfirmed'].includes(reply.errorCode ?? '')) {
      owned.draining = true; return;
    }
    if (['completed', 'cancelled', 'failed'].includes(reply.outcome)) this.owned.delete(owned.request.validationId);
  }
  private failed(request: MacroPackageLockedValidationCommand, errorCode: string): MacroPackageRunReply {
    return { requestId: request.command.requestId, runId: request.validationId, revision: request.validation.packageSha256,
      outcome: 'failed', inputsSent: false, errorCode } as MacroPackageRunReply;
  }
}
